import { useState } from "react";
import { Form, Row, Col, Alert, Button, Spinner } from "react-bootstrap";
import { Formik } from "formik";
import api from "../../../../../service/api";
import { tpmrFormSchema } from "./schema";
import { useNavigate } from "react-router-dom";

const TpmrForm = ({
    courseName,
    courseOptions,
    trainingDates,
    adhocTrainingDates,
    batchId,
    enrolmentId,
    studentId,
    setNotif,
}) => {
    const navigate = useNavigate();

    const [selectedDate, setSelectedDate] = useState("");
    const [selectedType, setSelectedType] = useState("");
    const [isEditMode, setIsEditMode] = useState(false);
    const [existingRecordId, setExistingRecordId] = useState(null);
    const [loadingRecord, setLoadingRecord] = useState(false);
    const [error, setError] = useState("");

    const practicalDates =
        trainingDates?.filter((date) => date.training_type === "practical") ||
        [];

    const adhocDates =
        adhocTrainingDates?.filter(
            (date) => date.training_type === "practical"
        ) || [];

    const allDates = [
        ...practicalDates.map((date, i) => ({
            ...date,
            type: "P",
            index: i + 1,
        })),
        ...adhocDates.map((date, i) => ({ ...date, type: "AP", index: i + 1 })),
    ];

    const getInitialCourseOptions = () => {
        const options = {};
        courseOptions?.forEach((index) => {
            options[index] = "";
        });
        return options;
    };

    const initialValues = {
        batch_id: batchId || "",
        enrolment_id: enrolmentId || "",
        student_id: studentId || "",
        practical_type: "",
        date: "",
        course_options: getInitialCourseOptions(),
        remarks: "",
    };

    const fetchExistingRecord = async (date, practicalType, setFieldValue) => {
        if (!date || !practicalType || !batchId || !enrolmentId || !studentId) {
            return;
        }

        setLoadingRecord(true);
        try {
            const response = await api.get("tms/student-tpmr", {
                batch_id: batchId,
                enrolment_id: enrolmentId,
                student_id: studentId,
                practical_type: practicalType,
                date: date,
            });

            if (response.data) {
                setIsEditMode(true);
                setExistingRecordId(response.data.id);

                const courseOptionsObj = getInitialCourseOptions();

                if (response.data.course_option_grade) {
                    response.data.course_option_grade.forEach((item) => {
                        const optionIndex = courseOptions.findIndex(
                            (opt) => opt === item.option
                        );
                        if (optionIndex !== -1) {
                            courseOptionsObj[optionIndex] = item.grade;
                        }
                    });
                }

                Object.entries(courseOptionsObj).forEach(([key, value]) => {
                    setFieldValue(`course_options.${key}`, value);
                });

                setFieldValue("remarks", response.data.remarks || "");
            } else {
                // NO EXISTING DATA
                setIsEditMode(false);
                setExistingRecordId(null);
                const resetOptions = getInitialCourseOptions();
                Object.keys(resetOptions).forEach((key) => {
                    setFieldValue(`course_options.${key}`, "");
                });
                setFieldValue("remarks", "");
            }
        } catch (error) {
            setIsEditMode(false);
            setExistingRecordId(null);
        } finally {
            setLoadingRecord(false);
        }
    };

    const handleDateChange = (e, setFieldValue) => {
        const dateValue = e.target.value;
        setSelectedDate(dateValue);

        const formattedDate = dateValue.split("-").reverse().join("-");
        setFieldValue("date", formattedDate);

        const dateItem = allDates.find((d) => d.date === dateValue);
        if (dateItem) {
            const typeValue = `${dateItem.type}${dateItem.index}`;
            setSelectedType(typeValue);
            setFieldValue("practical_type", typeValue);
            fetchExistingRecord(formattedDate, typeValue, setFieldValue);
        }
    };

    const handleTypeChange = (e, setFieldValue) => {
        const typeValue = e.target.value;
        setSelectedType(typeValue);
        setFieldValue("practical_type", typeValue);

        const match = typeValue.match(/^(AP|P)(\d+)$/);
        if (match) {
            const [, prefix, number] = match;
            const dateItem = allDates.find(
                (d) => d.type === prefix && d.index === parseInt(number)
            );

            if (dateItem) {
                setSelectedDate(dateItem.date);
                const formattedDate = dateItem.date
                    .split("-")
                    .reverse()
                    .join("-");
                setFieldValue("date", formattedDate);
                fetchExistingRecord(formattedDate, typeValue, setFieldValue);
            }
        } else if (selectedDate) {
            const formattedDate = selectedDate.split("-").reverse().join("-");
            fetchExistingRecord(formattedDate, typeValue, setFieldValue);
        }
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            setError("");

            const courseOptionsArray = [];

            Object.entries(values.course_options).forEach(([index, grade]) => {
                if (!grade || grade === "") return;
                const optionName = courseOptions[parseInt(index)];
                if (optionName && typeof grade === "string") {
                    courseOptionsArray.push({
                        option: optionName,
                        grade: grade,
                    });
                }
            });

            if (courseOptionsArray.length === 0) {
                setError("Please select at least one grade.");
                setSubmitting(false);
                return;
            }

            const payload = {
                batch_id: values.batch_id,
                enrolment_id: values.enrolment_id,
                student_id: values.student_id,
                practical_type: values.practical_type,
                date: values.date,
                course_options: courseOptionsArray,
                remarks: values.remarks || null,
            };

            if (isEditMode && existingRecordId) {
                await api.put(`tms/student-tpmr/${existingRecordId}`, payload);
                setNotif({
                    notifMsg: "TPMR updated successfully",
                    open: true,
                    severity: "success",
                });
            } else {
                await api.post("tms/student-tpmr", payload);
                setNotif({
                    notifMsg: "TPMR created successfully",
                    open: true,
                    severity: "success",
                });
            }

            resetForm();
            setSelectedDate("");
            setSelectedType("");
            setIsEditMode(false);
            setExistingRecordId(null);
        } catch (error) {
            console.error("Submit error:", error);
            console.error("Error response:", error.response?.data);
            setError(
                error.response?.data?.message ||
                    `Error ${
                        isEditMode ? "updating" : "creating"
                    } TPMR record. Please try again.`
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={tpmrFormSchema}
            onSubmit={handleSubmit}
            enableReinitialize
            validateOnChange={true}
            validateOnBlur={true}
        >
            {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit: formikHandleSubmit,
                setFieldValue,
                resetForm,
                isSubmitting,
            }) => {
                return (
                    <Form
                        onSubmit={(e) => {
                            e.preventDefault();
                            formikHandleSubmit(e);
                        }}
                    >
                        {isEditMode && (
                            <Alert variant="warning" className="mb-3 px-2 py-1">
                                <strong>Edit Mode: </strong> You are updating an
                                existing TPMR record for this date and practical
                                type.
                            </Alert>
                        )}

                        {loadingRecord && (
                            <Alert variant="info" className="mb-3">
                                <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-2"
                                />
                                Checking for existing record...
                            </Alert>
                        )}

                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Practical Type</Form.Label>
                                    <Form.Select
                                        name="practical_type"
                                        value={selectedType}
                                        onChange={(e) =>
                                            handleTypeChange(e, setFieldValue)
                                        }
                                        onBlur={handleBlur}
                                        isInvalid={
                                            touched.practical_type &&
                                            errors.practical_type
                                        }
                                        disabled={loadingRecord}
                                    >
                                        <option value="">
                                            Select training type
                                        </option>
                                        {practicalDates.map((_, i) => (
                                            <option
                                                key={`p-${i}`}
                                                value={`P${i + 1}`}
                                            >
                                                P{i + 1}
                                            </option>
                                        ))}
                                        {adhocDates.map((_, i) => (
                                            <option
                                                key={`ap-${i}`}
                                                value={`AP${i + 1}`}
                                            >
                                                AP{i + 1}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.practical_type}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Training Date</Form.Label>
                                    <Form.Select
                                        name="date"
                                        value={selectedDate}
                                        onChange={(e) =>
                                            handleDateChange(e, setFieldValue)
                                        }
                                        onBlur={handleBlur}
                                        isInvalid={touched.date && errors.date}
                                        disabled={loadingRecord}
                                    >
                                        <option value="">
                                            Select Training Date
                                        </option>
                                        {practicalDates.map((item, index) => (
                                            <option
                                                key={`p-${item.id}`}
                                                value={item.date}
                                            >
                                                {item.date}
                                            </option>
                                        ))}
                                        {adhocDates.map((item, index) => (
                                            <option
                                                key={`ap-${item.id}`}
                                                value={item.date}
                                            >
                                                {item.date}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.date}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <h3 className="fw-semibold py-2">{courseName}</h3>

                        <Row className="mb-3">
                            {courseOptions?.length > 0 ? (
                                courseOptions.map((trade, i) => (
                                    <Col md={4} key={i}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{trade}</Form.Label>
                                            <Form.Select
                                                name={`course_options.${i}`}
                                                value={
                                                    values.course_options?.[
                                                        i
                                                    ] || ""
                                                }
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                disabled={
                                                    !selectedDate ||
                                                    !selectedType ||
                                                    loadingRecord
                                                }
                                            >
                                                <option value="">
                                                    Select Grade
                                                </option>
                                                <option value="Poor">
                                                    Poor
                                                </option>
                                                <option value="NS">
                                                    Not Satisfactory
                                                </option>
                                                <option value="S">
                                                    Satisfactory
                                                </option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                ))
                            ) : (
                                <div className="px-3">
                                    <Alert variant="info">
                                        <div className="alert-message">
                                            No options for this course.
                                        </div>
                                    </Alert>
                                </div>
                            )}
                        </Row>
                        {error && (
                            <Alert variant="danger" className="p-3 fw-bold">
                                {error}
                            </Alert>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label>Remarks</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="remarks"
                                placeholder="Optional Remarks"
                                value={values.remarks}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={
                                    !selectedDate ||
                                    !selectedType ||
                                    loadingRecord
                                }
                            />
                        </Form.Group>

                        <div className="d-flex gap-2">
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={
                                    isSubmitting ||
                                    !selectedDate ||
                                    !selectedType ||
                                    loadingRecord
                                }
                            >
                                {isSubmitting ? (
                                    <>
                                        <Spinner
                                            animation="border"
                                            size="sm"
                                            className="me-2"
                                        />
                                        {isEditMode
                                            ? "Updating..."
                                            : "Submitting..."}
                                    </>
                                ) : isEditMode ? (
                                    "Update TPMR"
                                ) : (
                                    "Submit TPMR"
                                )}
                            </Button>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => {
                                    resetForm();
                                    setSelectedDate("");
                                    setSelectedType("");
                                    setIsEditMode(false);
                                    setExistingRecordId(null);
                                    setError("");
                                    navigate(
                                        `/tms/batches/students-tpmr/${batchId}`
                                    );
                                }}
                                disabled={isSubmitting || loadingRecord}
                            >
                                Cancel
                            </Button>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default TpmrForm;
